import { useCallback, useEffect, useMemo } from 'react'

import {
  QUERY_KEY_MANAGEMENT_GET_TASK_QUEUE,
  QUERY_KEY_MANAGEMENT_SEARCH_UNIT_TASK_QUEUE,
  QUERY_KEY_MANAGEMENT_SEARCH_UNIT_TASK_QUEUE_INFINITE
} from '@apps-common/apis/management/unit-task/task-queue'
import { getTaskQueue } from '@apps-common/apis/management/unit-task/task-queue/get-task-queue'
import { UNIT_TASK_UPDATE_LISTENER_STATUSES, UnitTaskStatus } from '@apps-common/config/unit-task'
import { useTimerCallback } from '@apps-common/hooks/useTimerCallback'
import { queryClient } from '@apps-common/lib'
import { IUnitTaskQueueModel } from '@apps-common/types/portal/unit-tasks'

import { useTaskQueueNotificationListener } from './useTaskQueueNotificationListener'

export const useTaskQueueUpdates = (containerId: string, queueLoading: boolean, allLoading: boolean, queue: IUnitTaskQueueModel[]) => {
  useEffect(() => {
    const element = document.getElementById(containerId)

    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }, [queueLoading])

  const updateTaskQueueInCache = useCallback((updated: IUnitTaskQueueModel) => {
    queryClient.setQueryData(QUERY_KEY_MANAGEMENT_GET_TASK_QUEUE(updated.id), updated)

    const listQueries = queryClient.getQueryCache().findAll({ queryKey: QUERY_KEY_MANAGEMENT_SEARCH_UNIT_TASK_QUEUE() })
    listQueries.forEach((query) => {
      queryClient.setQueryData(query.queryKey, (data: IUnitTaskQueueModel[] | undefined) => {
        if (!Array.isArray(data)) {
          return data
        }

        const index = data.findIndex((item) => item.id === updated.id)
        if (index === -1) {
          return data
        }

        return data.map((item) => (item.id === updated.id ? updated : item))
      })
    })

    const infiniteQueries = queryClient.getQueryCache().findAll({ queryKey: QUERY_KEY_MANAGEMENT_SEARCH_UNIT_TASK_QUEUE_INFINITE() })
    infiniteQueries.forEach((query) => {
      queryClient.setQueryData(query.queryKey, (data: any) => {
        if (!data || !Array.isArray(data.pages)) {
          return data
        }

        const pages = data.pages.map((page: IUnitTaskQueueModel[]) => {
          if (!Array.isArray(page)) {
            return page
          }

          const index = page.findIndex((item) => item.id === updated.id)
          if (index === -1) {
            return page
          }

          return page.map((item) => (item.id === updated.id ? updated : item))
        })

        return { ...data, pages }
      })
    })
  }, [])

  const patchTaskQueueStatus = useCallback((taskQueueId: number, status: string) => {
    const applyStatus = (item: IUnitTaskQueueModel) => ({ ...item, status })

    queryClient.setQueryData(QUERY_KEY_MANAGEMENT_GET_TASK_QUEUE(taskQueueId), (data: IUnitTaskQueueModel | undefined) =>
      data ? applyStatus(data) : data
    )

    const listQueries = queryClient.getQueryCache().findAll({ queryKey: QUERY_KEY_MANAGEMENT_SEARCH_UNIT_TASK_QUEUE() })
    listQueries.forEach((query) => {
      queryClient.setQueryData(query.queryKey, (data: IUnitTaskQueueModel[] | undefined) => {
        if (!Array.isArray(data)) {
          return data
        }

        const index = data.findIndex((item) => item.id === taskQueueId)
        if (index === -1) {
          return data
        }

        return data.map((item) => (item.id === taskQueueId ? applyStatus(item) : item))
      })
    })

    const infiniteQueries = queryClient.getQueryCache().findAll({ queryKey: QUERY_KEY_MANAGEMENT_SEARCH_UNIT_TASK_QUEUE_INFINITE() })
    infiniteQueries.forEach((query) => {
      queryClient.setQueryData(query.queryKey, (data: any) => {
        if (!data || !Array.isArray(data.pages)) {
          return data
        }

        const pages = data.pages.map((page: IUnitTaskQueueModel[]) => {
          if (!Array.isArray(page)) {
            return page
          }

          const index = page.findIndex((item) => item.id === taskQueueId)
          if (index === -1) {
            return page
          }

          return page.map((item) => (item.id === taskQueueId ? applyStatus(item) : item))
        })

        return { ...data, pages }
      })
    })
  }, [])

  const refreshCb = useCallback(
    async (taskQueueId: number) => {
      try {
        const updated = await queryClient.fetchQuery({
          queryKey: QUERY_KEY_MANAGEMENT_GET_TASK_QUEUE(taskQueueId),
          queryFn: () => getTaskQueue(taskQueueId)
        })

        if (updated) {
          updateTaskQueueInCache(updated)
        }
      } catch (error) {
        queryClient.refetchQueries({ queryKey: QUERY_KEY_MANAGEMENT_GET_TASK_QUEUE(taskQueueId) })
      }
    },
    [updateTaskQueueInCache]
  )

  const pendingQueue = useMemo(() => {
    if (queueLoading || allLoading) {
      return []
    }

    return queue?.filter((q) => UNIT_TASK_UPDATE_LISTENER_STATUSES.includes(q.status)) || []
  }, [allLoading, queue, queueLoading])

  const getIntervalMs = (count: number) => {
    const extraDelay = Math.min(Math.max(count - 1, 0) * 1000, 30000)
    return 15000 + extraDelay
  }

  const processQueue = useCallback(() => {
    const run = async () => {
      const staggerDelayMs = Math.min(1000 + pendingQueue.length * 250, 5000)

      for (let i = 0; i < pendingQueue.length; i += 1) {
        const item = pendingQueue[i]
        await refreshCb(item.id)
        await new Promise((resolve) => setTimeout(resolve, staggerDelayMs))
      }
    }

    void run()
  }, [pendingQueue, refreshCb])

  useTimerCallback(processQueue, getIntervalMs(pendingQueue.length), pendingQueue.length > 0)
  useTaskQueueNotificationListener(pendingQueue || [], (event) => {
    const taskQueueId = parseInt(event?.data?.taskQueueId || '', 10)
    const nextStatus = (event?.data?.status || event?.data?.action || '').toString()

    if (!Number.isNaN(taskQueueId)) {
      console.log('[notif-update] task-queue refresh', taskQueueId)
      if (nextStatus && Object.values(UnitTaskStatus).includes(nextStatus)) {
        patchTaskQueueStatus(taskQueueId, nextStatus)
      }
      refreshCb(taskQueueId)
      return
    }

    processQueue()
  })
}
